/* ==========================================================================
   AGE Eyewear — section behaviour (vanilla, no dependencies)
   Replaces framer-motion / lenis / react state from the Emergent build.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Scroll reveal ------------------------------------------------------ */

  var revealObserver = null;

  function initReveals(root) {
    var nodes = (root || document).querySelectorAll('.age-reveal:not(.is-visible)');
    if (!nodes.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) {
        n.classList.add('is-visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
      );
    }

    nodes.forEach(function (n) {
      revealObserver.observe(n);
    });
  }

  /* --- Hero parallax ------------------------------------------------------ */

  function initParallax(root) {
    if (reduceMotion) return;
    var nodes = (root || document).querySelectorAll('[data-age-parallax]');
    if (!nodes.length) return;

    var ticking = false;

    function update() {
      nodes.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var vh = window.innerHeight || 1;
        if (rect.bottom < 0 || rect.top > vh) return;
        var progress = 1 - (rect.top + rect.height) / (vh + rect.height);
        var amount = parseFloat(el.dataset.ageParallax) || 12;
        el.style.transform = 'translate3d(0,' + (progress * amount).toFixed(2) + '%,0) scale(1.12)';
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* --- Smooth in-page anchors -------------------------------------------- */

  function initAnchors() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href*="#"]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      var hashIndex = href.indexOf('#');
      if (hashIndex === -1) return;

      var hash = href.slice(hashIndex);
      if (hash.length < 2) return;

      // only intercept links pointing at the current page
      var path = href.slice(0, hashIndex);
      if (path && path !== window.location.pathname && path !== '/' + window.location.pathname) {
        if (path.indexOf('http') === 0) return;
        if (path !== window.location.pathname) return;
      }

      var target = document.querySelector(hash);
      if (!target) return;

      event.preventDefault();
      var header = document.querySelector('.header-wrapper');
      var offset = header ? header.getBoundingClientRect().height + 16 : 90;
      var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      history.replaceState(null, '', hash);
    });
  }

  /* --- Strength finder ---------------------------------------------------- */

  function initFinder(root) {
    (root || document).querySelectorAll('[data-age-finder]').forEach(function (el) {
      if (el.dataset.ageBound === 'true') return;
      el.dataset.ageBound = 'true';

      var state = { age: null, symptom: null };
      var result = el.querySelector('[data-age-finder-result]');
      var value = el.querySelector('[data-age-finder-value]');

      function render() {
        var ready = state.age !== null && state.symptom !== null;
        if (!ready) {
          result.hidden = true;
          return;
        }
        var total = Math.min(3, state.age + state.symptom);
        value.textContent = '+' + total.toFixed(2);
        result.hidden = false;
      }

      el.querySelectorAll('[data-age-finder-opt]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var group = btn.dataset.ageFinderOpt;
          var scope = el.querySelectorAll('[data-age-finder-opt="' + group + '"]');
          scope.forEach(function (b) {
            b.setAttribute('aria-pressed', String(b === btn));
          });
          state[group] = parseFloat(btn.dataset.ageValue) || 0;
          render();
        });
      });

      var reset = el.querySelector('[data-age-finder-reset]');
      if (reset) {
        reset.addEventListener('click', function () {
          state.age = null;
          state.symptom = null;
          el.querySelectorAll('[data-age-finder-opt]').forEach(function (b) {
            b.setAttribute('aria-pressed', 'false');
          });
          render();
        });
      }
    });
  }

  /* --- Stockists ---------------------------------------------------------- */

  function initStockists(root) {
    (root || document).querySelectorAll('[data-age-stockists]').forEach(function (el) {
      if (el.dataset.ageBound === 'true') return;
      el.dataset.ageBound = 'true';

      var filter = 'all';
      var selected = null;
      var cities = Array.prototype.slice.call(el.querySelectorAll('[data-age-city]'));
      var markers = Array.prototype.slice.call(el.querySelectorAll('[data-age-marker]'));
      var intl = el.querySelector('[data-age-international]');

      function render() {
        cities.forEach(function (card) {
          var island = card.dataset.ageIsland;
          var city = card.dataset.ageCity;
          var matchesFilter = filter === 'all' || filter === island;
          var matchesSelection = !selected || selected === city;
          card.hidden = filter === 'international' || !matchesFilter || !matchesSelection;
          card.classList.toggle('is-active', selected === city);
        });

        markers.forEach(function (marker) {
          var island = marker.dataset.ageIsland;
          var city = marker.dataset.ageCity;
          var visible = filter === 'all' || filter === island;
          marker.style.display = visible ? '' : 'none';
          marker.classList.toggle('is-active', selected === city);
        });

        if (intl) intl.hidden = filter !== 'international';
      }

      el.querySelectorAll('[data-age-filter]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          filter = btn.dataset.ageFilter;
          selected = null;
          el.querySelectorAll('[data-age-filter]').forEach(function (b) {
            b.setAttribute('aria-pressed', String(b === btn));
          });
          render();
        });
      });

      function toggleCity(city) {
        selected = selected === city ? null : city;
        render();
      }

      cities.forEach(function (card) {
        card.addEventListener('click', function () {
          toggleCity(card.dataset.ageCity);
        });
      });

      markers.forEach(function (marker) {
        marker.addEventListener('click', function () {
          toggleCity(marker.dataset.ageCity);
        });
      });

      render();
    });
  }

  /* --- Boot --------------------------------------------------------------- */

  function initAll(root) {
    initReveals(root);
    initParallax(root);
    initFinder(root);
    initStockists(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
      initAnchors();
    });
  } else {
    initAll(document);
    initAnchors();
  }

  // Theme editor: re-bind when a section is re-rendered
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
